<template>
  <section class="opportunities-summary">
    <div v-if="isLoading" class="opportunities-summary__card">
      <woot-loading-state
        :message="$t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.LOADING')"
      />
    </div>
    <p
      v-else-if="hasError"
      class="opportunities-summary__card opportunities-summary__error"
    >
      {{ $t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.ERROR') }}
    </p>
    <opportunity-dashboard-open-card
      v-else-if="summary && summary.open.count"
      class="opportunities-summary__open"
      :open="summary.open"
      :sla-breakdown="summary.open.sla_breakdown"
    />
    <div
      v-else-if="summary && summary.has_pipelines"
      class="opportunities-summary__card opportunities-summary__empty"
    >
      <img
        :src="noOpenOpportunitiesImage"
        alt=""
        class="opportunities-summary__image"
      />
      <div class="opportunities-summary__content">
        <p>{{ $t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_OPEN.MESSAGE') }}</p>
        <p>{{ $t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_OPEN.REAL_TIME') }}</p>
        <p>
          <span aria-hidden="true">🎯</span>
          {{ $t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_OPEN.CALL_TO_ACTION') }}
        </p>
      </div>
    </div>
    <div
      v-else
      class="opportunities-summary__card opportunities-summary__empty"
    >
      <img
        :src="noPipelinesImage"
        alt=""
        class="opportunities-summary__image"
      />
      <div class="opportunities-summary__content">
        <h4 class="opportunities-summary__title">
          <span aria-hidden="true">🚀</span>
          {{ $t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_PIPELINES.TITLE') }}
        </h4>
        <ul class="opportunities-summary__features">
          <li v-for="feature in features" :key="feature.key">
            <span aria-hidden="true">{{ feature.emoji }}</span>
            <strong>{{ feature.title }}</strong>
            {{ feature.description }}
          </li>
        </ul>
        <router-link
          v-if="canCreatePipeline"
          class="button nice opportunities-summary__create"
          :to="{ name: 'settings_opportunity_pipelines_new' }"
        >
          {{ $t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_PIPELINES.CREATE') }}
        </router-link>
        <span
          v-else
          v-tooltip.top="
            $t(
              'OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_PIPELINES.CONTACT_MANAGER'
            )
          "
          class="opportunities-summary__create opportunities-summary__create--disabled"
          tabindex="0"
        >
          <button type="button" class="button nice" disabled>
            {{ $t('OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_PIPELINES.CREATE') }}
          </button>
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import OpportunityDashboardOpenCard from 'dashboard/routes/dashboard/opportunities/components/dashboard/OpportunityDashboardOpenCard.vue';
import noPipelinesImage from 'dashboard/assets/images/opportunity-pipelines/home-no-pipelines.svg';
import noOpenOpportunitiesImage from 'dashboard/assets/images/opportunity-pipelines/home-no-open-opportunities.svg';

const FEATURES = [
  { key: 'PIPELINES', emoji: '🎯' },
  { key: 'KANBAN', emoji: '📈' },
  { key: 'DASHBOARD', emoji: '📊' },
  { key: 'LOSS_REASONS', emoji: '🔍' },
];

export default {
  name: 'OpportunitiesSummary',
  components: { OpportunityDashboardOpenCard },
  data() {
    return {
      isLoading: true,
      hasError: false,
      summary: null,
      noPipelinesImage,
      noOpenOpportunitiesImage,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isPipelinesManagementAllowed:
        'accounts/isOpportunityPipelinesManagementAllowed',
    }),
    isOpportunityModuleEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.OPPORTUNITY_PIPELINES
      );
    },
    canCreatePipeline() {
      return this.isPipelinesManagementAllowed(this.accountId);
    },
    features() {
      return FEATURES.map(({ key, emoji }) => ({
        key,
        emoji,
        title: this.$t(
          `OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_PIPELINES.FEATURES.${key}.TITLE`
        ),
        description: this.$t(
          `OPERATIONAL_DASHBOARD.OPPORTUNITIES.NO_PIPELINES.FEATURES.${key}.DESCRIPTION`
        ),
      }));
    },
  },
  mounted() {
    this.fetchSummary();
  },
  methods: {
    async fetchSummary() {
      if (!this.isOpportunityModuleEnabled) {
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      this.hasError = false;
      try {
        this.summary = await this.$store.dispatch('opportunities/summary');
      } catch (error) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.opportunities-summary__card {
  background: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  margin: 0;
  padding: var(--space-medium) var(--space-large);
}

.opportunities-summary__open {
  border-color: var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-medium) var(--space-large);
}

.opportunities-summary__error {
  color: var(--s-600);
  text-align: center;
}

.opportunities-summary__empty {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-large) var(--space-larger);
  justify-content: center;
}

.opportunities-summary__image {
  flex-shrink: 0;
  max-width: 100%;
  width: 26rem;
}

.opportunities-summary__content {
  color: var(--s-900);
  display: flex;
  flex: 1 1 28rem;
  flex-direction: column;
  font-size: var(--font-size-default);
  gap: var(--space-normal);
  max-width: 64rem;

  p {
    margin: 0;
  }
}

.opportunities-summary__title {
  font-size: var(--font-size-large);
  font-weight: var(--font-weight-bold);
  margin: 0 0 var(--space-small);
}

.opportunities-summary__features {
  list-style: none;
  margin: 0;

  li {
    line-height: 1.6;
  }
}

.opportunities-summary__create {
  align-self: flex-end;
  margin: var(--space-normal) 0 0;
}

.opportunities-summary__create--disabled {
  cursor: not-allowed;

  .button {
    margin: 0;
    pointer-events: none;
  }
}
</style>
